import { z } from 'zod';

import { purchaseOperatingModeValues } from './purchases.dto.js';
import {
  purchaseSettlementReferenceCounterMethodValues,
  purchaseSettlementReferenceCounterScopeValues,
  purchaseSettlementReferenceModeValues,
  purchaseSettlementMethodValues,
  purchaseSettlementPolicySourceValues,
  purchaseSettlementRuntimeMethodValues,
  purchaseSettlementStatusValues,
  type PurchaseSettlementAllowedOperatingModes,
  type PurchaseSettlementAllocation,
  type PurchaseSettlementAvailability,
  type PurchaseSettlementApprovalFlags,
  type PurchaseSettlementAutoReferenceConfig,
  type PurchaseSettlementAutoReferenceMethodConfig,
  type PurchaseSettlementManualReferenceAllowance,
  type PurchaseSettlementMethodAvailability,
  type PurchaseSettlementMethodMetadata,
  type PurchaseSettlementOneActionEligibility,
  type PurchaseSettlementPolicyConfig,
  type PurchaseSettlementPolicyOverride,
  type PurchaseSettlementReferenceCounter,
  type PurchaseSettlementRequiredPermissions,
  type PurchaseSettlementSelectionInput,
  type PurchaseSettlementSummary,
} from './purchases-settlement.dto.js';

const timestampSchema = z.iso.datetime();

export const PurchaseSettlementMethodSchema = z.enum(purchaseSettlementMethodValues);
export const PurchaseSettlementRuntimeMethodSchema = z.enum(purchaseSettlementRuntimeMethodValues);
export const PurchaseSettlementStatusSchema = z.enum(purchaseSettlementStatusValues);
export const PurchaseSettlementPolicySourceSchema = z.enum(purchaseSettlementPolicySourceValues);
export const PurchaseSettlementReferenceModeSchema = z.enum(purchaseSettlementReferenceModeValues);
export const PurchaseSettlementReferenceCounterScopeSchema = z.enum(
  purchaseSettlementReferenceCounterScopeValues,
);
export const PurchaseSettlementReferenceCounterMethodSchema = z.enum(
  purchaseSettlementReferenceCounterMethodValues,
);

const referenceTextSchema = z.string().trim().min(1).max(128);

export const PurchaseSettlementSelectionInputSchema: z.ZodType<PurchaseSettlementSelectionInput> =
  z.object({
    method: PurchaseSettlementMethodSchema,
    reference: referenceTextSchema.nullable().optional(),
    referenceMode: PurchaseSettlementReferenceModeSchema.nullable().optional(),
  });

const PurchaseCheckSettlementMetadataSchema = z.object({
  method: z.literal('check'),
  referenceLabel: z.string().trim().min(1).max(40),
  referenceNumber: referenceTextSchema,
});

const PurchaseVoucherSettlementMetadataSchema = z.object({
  method: z.literal('voucher'),
  referenceLabel: z.string().trim().min(1).max(40),
  referenceNumber: referenceTextSchema,
  redeemableAt: z.string().trim().min(1).max(200),
});

export const PurchaseSettlementMethodMetadataSchema: z.ZodType<PurchaseSettlementMethodMetadata> =
  z.discriminatedUnion('method', [
    PurchaseCheckSettlementMetadataSchema,
    PurchaseVoucherSettlementMetadataSchema,
  ]);

export const PurchaseSettlementAllocationSchema: z.ZodType<PurchaseSettlementAllocation> = z
  .object({
    id: z.string().trim().min(1).max(128),
    method: PurchaseSettlementMethodSchema,
    status: PurchaseSettlementStatusSchema,
    amount: z.number().min(0),
    reference: referenceTextSchema.nullable().optional(),
    cashSessionId: z.uuid().nullable().optional(),
    cashLocationId: z.uuid().nullable().optional(),
    cashMovementId: z.uuid().nullable().optional(),
    settledAt: timestampSchema.nullable().optional(),
    createdAt: timestampSchema.nullable().optional(),
  })
  .refine(
    (value) =>
      value.method !== 'cash' ||
      (Boolean(value.cashSessionId) && Boolean(value.cashLocationId)),
    {
      message: 'Cash allocations require cash session and cash location links.',
      path: ['cashSessionId'],
    },
  )
  .refine(
    (value) =>
      value.method === 'cash' ||
      (!value.cashSessionId && !value.cashLocationId && !value.cashMovementId),
    {
      message: 'Cash links are only valid for cash allocations.',
      path: ['method'],
    },
  )
  .refine(
    (value) =>
      (value.method !== 'check' && value.method !== 'voucher') || Boolean(value.reference),
    {
      message: 'Check and voucher allocations require a settlement reference.',
      path: ['reference'],
    },
  );

export const PurchaseSettlementSummarySchema: z.ZodType<PurchaseSettlementSummary> = z
  .object({
    requestedMethod: PurchaseSettlementMethodSchema.nullable(),
    resolvedMethod: PurchaseSettlementMethodSchema,
    status: PurchaseSettlementStatusSchema,
    totalAmount: z.number().min(0),
    settledAmount: z.number().min(0),
    pendingAmount: z.number().min(0),
    methodMetadata: PurchaseSettlementMethodMetadataSchema.nullable().optional(),
    allocations: z.array(PurchaseSettlementAllocationSchema).min(1),
  })
  .refine(
    (value) =>
      value.allocations.reduce((total, allocation) => total + allocation.amount, 0) <=
      value.totalAmount + 0.0001,
    {
      message: 'Settlement allocations cannot exceed total amount.',
      path: ['allocations'],
    },
  )
  .refine((value) => ['cash', 'check', 'voucher'].includes(value.resolvedMethod), {
    message: 'Only cash, check, and voucher settlement summaries are supported.',
    path: ['resolvedMethod'],
  })
  .refine(
    (value) =>
      value.resolvedMethod !== 'cash' ||
      value.allocations.every((allocation) => allocation.method === 'cash'),
    {
      message: 'Cash settlement summaries require cash allocations.',
      path: ['allocations'],
    },
  )
  .refine(
    (value) =>
      value.resolvedMethod !== 'check' ||
      (value.methodMetadata?.method === 'check' &&
        value.allocations.every((allocation) => allocation.method === 'check')),
    {
      message: 'Check settlement summaries require check metadata and check allocations.',
      path: ['methodMetadata'],
    },
  )
  .refine(
    (value) =>
      (value.resolvedMethod !== 'check' && value.resolvedMethod !== 'voucher') ||
      (Math.abs(value.totalAmount - value.settledAmount) <= 0.0001 &&
        Math.abs(value.pendingAmount) <= 0.0001 &&
        Math.abs(
          value.allocations.reduce((total, allocation) => total + allocation.amount, 0) -
            value.totalAmount,
        ) <= 0.0001),
    {
      message: 'Check and voucher settlement summaries must settle the full purchase amount.',
      path: ['settledAmount'],
    },
  )
  .refine(
    (value) =>
      (value.resolvedMethod !== 'check' && value.resolvedMethod !== 'voucher') ||
      (['issued', 'voided'].includes(value.status) &&
        value.allocations.every((allocation) =>
          ['issued', 'voided'].includes(allocation.status),
        )),
    {
      message: 'Check and voucher settlement summaries must use issued or voided status.',
      path: ['status'],
    },
  )
  .refine(
    (value) =>
      value.resolvedMethod !== 'voucher' ||
      (value.methodMetadata?.method === 'voucher' &&
        value.allocations.every((allocation) => allocation.method === 'voucher')),
    {
      message: 'Voucher settlement summaries require voucher metadata and voucher allocations.',
      path: ['methodMetadata'],
    },
  );

export const PurchaseSettlementPolicyOverrideSchema: z.ZodType<PurchaseSettlementPolicyOverride> =
  z
    .object({
      scope: z.enum(['location', 'station'] as const),
      locationId: z.uuid().nullable().optional(),
      stationId: z.uuid().nullable().optional(),
      defaultMethod: PurchaseSettlementRuntimeMethodSchema.nullable().optional(),
      enabledMethods: z.array(PurchaseSettlementRuntimeMethodSchema).optional(),
    })
    .refine((value) => (value.scope === 'location' ? Boolean(value.locationId) : true), {
      message: 'locationId is required for location settlement policy overrides.',
      path: ['locationId'],
    })
    .refine((value) => (value.scope === 'station' ? Boolean(value.stationId) : true), {
      message: 'stationId is required for station settlement policy overrides.',
      path: ['stationId'],
    });

export const PurchaseSettlementApprovalFlagsSchema: z.ZodType<PurchaseSettlementApprovalFlags> =
  z.object({
    checkPayoutApproved: z.boolean(),
    voucherPayoutApproved: z.boolean(),
    checkDefaultApproved: z.boolean(),
    voucherDefaultApproved: z.boolean(),
    manualReferenceFastPayApproved: z.boolean(),
    trainingModeApproved: z.boolean(),
  });

export const PurchaseSettlementOneActionEligibilitySchema: z.ZodType<PurchaseSettlementOneActionEligibility> =
  z.object({
    cash: z.boolean(),
    check: z.boolean(),
    voucher: z.boolean(),
  });

export const PurchaseSettlementManualReferenceAllowanceSchema: z.ZodType<PurchaseSettlementManualReferenceAllowance> =
  z.object({
    check: z.boolean(),
    voucher: z.boolean(),
    allowedAsOneActionDefault: z.boolean(),
  });

export const PurchaseSettlementAutoReferenceMethodConfigSchema: z.ZodType<PurchaseSettlementAutoReferenceMethodConfig> =
  z.object({
    enabled: z.boolean(),
    counterId: z.uuid().nullable().optional(),
    prefix: z.string().trim().min(1).max(24).nullable().optional(),
    padding: z.number().int().min(1).max(12).nullable().optional(),
  });

export const PurchaseSettlementAutoReferenceConfigSchema: z.ZodType<PurchaseSettlementAutoReferenceConfig> =
  z.object({
    check: PurchaseSettlementAutoReferenceMethodConfigSchema,
    voucher: PurchaseSettlementAutoReferenceMethodConfigSchema,
  });

export const PurchaseSettlementAllowedOperatingModesSchema: z.ZodType<PurchaseSettlementAllowedOperatingModes> =
  z.object({
    cash: z.array(z.enum(purchaseOperatingModeValues)),
    check: z.array(z.enum(purchaseOperatingModeValues)),
    voucher: z.array(z.enum(purchaseOperatingModeValues)),
  });

const permissionKeySchema = z.string().trim().min(1).max(120);

export const PurchaseSettlementRequiredPermissionsSchema: z.ZodType<PurchaseSettlementRequiredPermissions> =
  z.object({
    check: z.array(permissionKeySchema),
    voucher: z.array(permissionKeySchema),
    override: z.array(permissionKeySchema),
    policyAdmin: z.array(permissionKeySchema),
  });

export const PurchaseSettlementPolicyConfigSchema: z.ZodType<PurchaseSettlementPolicyConfig> =
  z
    .object({
      companyId: z.uuid().nullable().optional(),
      companyDefaultMethod: PurchaseSettlementRuntimeMethodSchema.nullable().optional(),
      locationOverrides: z.array(PurchaseSettlementPolicyOverrideSchema).optional(),
      stationOverrides: z.array(PurchaseSettlementPolicyOverrideSchema).optional(),
      enabledMethods: z.array(PurchaseSettlementRuntimeMethodSchema).nonempty(),
      defaultMethod: PurchaseSettlementRuntimeMethodSchema,
      source: PurchaseSettlementPolicySourceSchema,
      systemFallbackMethod: z.literal('cash'),
      approvalFlags: PurchaseSettlementApprovalFlagsSchema,
      oneActionEligibility: PurchaseSettlementOneActionEligibilitySchema,
      manualReferenceAllowance: PurchaseSettlementManualReferenceAllowanceSchema,
      autoReference: PurchaseSettlementAutoReferenceConfigSchema,
      voucherRedeemableAt: z.string().trim().min(1).max(200).nullable().optional(),
      allowedOperatingModes: PurchaseSettlementAllowedOperatingModesSchema,
      requiredPermissions: PurchaseSettlementRequiredPermissionsSchema,
    })
    .superRefine((value, ctx) => {
      if (!value.enabledMethods.includes(value.defaultMethod)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Default settlement method must be enabled by policy.',
          path: ['defaultMethod'],
        });
      }

      if (value.enabledMethods.includes('check') && !value.approvalFlags.checkPayoutApproved) {
        ctx.addIssue({
          code: 'custom',
          message: 'Check settlement cannot be enabled without check payout approval.',
          path: ['approvalFlags', 'checkPayoutApproved'],
        });
      }

      if (value.enabledMethods.includes('voucher') && !value.approvalFlags.voucherPayoutApproved) {
        ctx.addIssue({
          code: 'custom',
          message: 'Voucher settlement cannot be enabled without voucher payout approval.',
          path: ['approvalFlags', 'voucherPayoutApproved'],
        });
      }

      if (value.defaultMethod === 'check' && !value.approvalFlags.checkDefaultApproved) {
        ctx.addIssue({
          code: 'custom',
          message: 'Check cannot be the default settlement method without default approval.',
          path: ['approvalFlags', 'checkDefaultApproved'],
        });
      }

      if (value.defaultMethod === 'voucher' && !value.approvalFlags.voucherDefaultApproved) {
        ctx.addIssue({
          code: 'custom',
          message: 'Voucher cannot be the default settlement method without default approval.',
          path: ['approvalFlags', 'voucherDefaultApproved'],
        });
      }

      if (
        value.manualReferenceAllowance.allowedAsOneActionDefault &&
        !value.approvalFlags.manualReferenceFastPayApproved
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Manual references cannot qualify for one-action default Pay without approval.',
          path: ['manualReferenceAllowance', 'allowedAsOneActionDefault'],
        });
      }

      if (value.oneActionEligibility.check && !value.autoReference.check.enabled) {
        ctx.addIssue({
          code: 'custom',
          message: 'Check one-action eligibility requires check auto-reference configuration.',
          path: ['autoReference', 'check', 'enabled'],
        });
      }

      if (value.oneActionEligibility.voucher && !value.autoReference.voucher.enabled) {
        ctx.addIssue({
          code: 'custom',
          message: 'Voucher one-action eligibility requires voucher auto-reference configuration.',
          path: ['autoReference', 'voucher', 'enabled'],
        });
      }

      if (value.oneActionEligibility.voucher && !value.voucherRedeemableAt) {
        ctx.addIssue({
          code: 'custom',
          message: 'Voucher one-action eligibility requires redeemable-at display text.',
          path: ['voucherRedeemableAt'],
        });
      }

      if (
        (value.allowedOperatingModes.check.includes('training') ||
          value.allowedOperatingModes.voucher.includes('training')) &&
        !value.approvalFlags.trainingModeApproved
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Check/voucher training-mode use requires explicit approval.',
          path: ['approvalFlags', 'trainingModeApproved'],
        });
      }
    });

export const PurchaseSettlementMethodAvailabilitySchema: z.ZodType<PurchaseSettlementMethodAvailability> =
  z.object({
    method: PurchaseSettlementMethodSchema,
    enabled: z.boolean(),
    available: z.boolean(),
    oneActionEligible: z.boolean(),
    referenceModes: z.array(PurchaseSettlementReferenceModeSchema).optional(),
    unavailableReason: z.string().trim().min(1).max(128).nullable().optional(),
    availableInModes: z.array(z.enum(purchaseOperatingModeValues)).optional(),
  });

export const PurchaseSettlementAvailabilitySchema: z.ZodType<PurchaseSettlementAvailability> =
  z.object({
    requestedMethod: PurchaseSettlementMethodSchema.nullable(),
    defaultMethod: PurchaseSettlementMethodSchema,
    resolvedMethod: PurchaseSettlementMethodSchema,
    enabledMethods: z.array(PurchaseSettlementMethodSchema).nonempty(),
    source: PurchaseSettlementPolicySourceSchema,
    methods: z.array(PurchaseSettlementMethodAvailabilitySchema).nonempty(),
  });

export const PurchaseSettlementReferenceCounterSchema: z.ZodType<PurchaseSettlementReferenceCounter> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      scope: PurchaseSettlementReferenceCounterScopeSchema,
      locationId: z.uuid().nullable().optional(),
      stationId: z.uuid().nullable().optional(),
      method: PurchaseSettlementReferenceCounterMethodSchema,
      prefix: z.string().trim().min(1).max(24).nullable().optional(),
      nextNumber: z.number().int().positive(),
      padding: z.number().int().min(1).max(12),
      enabled: z.boolean(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
    })
    .refine((value) => (value.scope === 'location' ? Boolean(value.locationId) : true), {
      message: 'locationId is required for location reference counters.',
      path: ['locationId'],
    })
    .refine((value) => (value.scope === 'station' ? Boolean(value.stationId) : true), {
      message: 'stationId is required for station reference counters.',
      path: ['stationId'],
    });
